import type { ReactNode } from "react";
import { X } from "lucide-react";

import { CarrierLogo } from "@/components/common/CarrierLogo";
import { PortAreaMatchBadge } from "@/components/common/PortAreaMatch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatRouteLabel } from "@/lib/formatPort";
import {
  classifyAreaMatch,
  type AreaMatcher,
} from "@/lib/portAreaMatchUtils";
import {
  normalizeCutoffName,
  parseCutOffDates,
} from "@/lib/scheduleCutoffs";
import { formatDateTimePlain } from "@/lib/weekDates";
import { ScheduleSailingMatchedRatesPanel } from "@/features/schedule/ScheduleSailingMatchedRates";
import type { MatchedScheduleRates } from "@/lib/scheduleRateMatch";
import type { CutOffDate, ScheduleLegResponse, ScheduleResponse } from "@/types/api";
import { formatBookingDate } from "@/types/booking";
import { useUserPreferences } from "@/context/UserPreferencesContext";

function carrierCode(s: ScheduleResponse): string {
  return s.carrier_canonical_code || s.carrier_name;
}

function sailingEtd(s: ScheduleResponse): string | null {
  const iso = s.origin_estimated_date ?? s.legs[0]?.departure_estimated_date;
  return iso ? iso.slice(0, 10) : null;
}

function sailingEta(s: ScheduleResponse): string | null {
  const last = s.legs[s.legs.length - 1];
  const iso = s.dest_estimated_date ?? last?.arrival_estimated_date;
  return iso ? iso.slice(0, 10) : null;
}

function sailingTransitDays(s: ScheduleResponse): number | null {
  if (s.transit_time != null && s.transit_time >= 0) {
    return s.transit_time;
  }
  const etd = sailingEtd(s);
  const eta = sailingEta(s);
  if (!etd || !eta) return null;
  const start = new Date(`${etd}T12:00:00`);
  const end = new Date(`${eta}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000);
  return days > 0 ? days : null;
}

function formatTransitDays(days: number): string {
  return `${days} ${days === 1 ? "day" : "days"}`;
}

function formatLegPort(
  locode: string | null | undefined,
  name: string | null | undefined,
  showLocodes: boolean,
): string {
  if (name?.trim()) {
    const code = locode?.trim();
    if (showLocodes && code && name.trim() !== code) {
      return `${name.trim()} (${code})`;
    }
    return name.trim();
  }
  return locode?.trim() || "—";
}

function collectCutoffs(sailing: ScheduleResponse): CutOffDate[] {
  const seen = new Set<string>();
  const out: CutOffDate[] = [];

  const add = (items: CutOffDate[]) => {
    for (const c of items) {
      if (!c.date_time) continue;
      const key = `${normalizeCutoffName(c.name)}|${c.date_time}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
  };

  add(parseCutOffDates(sailing.cut_off_dates));
  for (const leg of sailing.legs) {
    add(parseCutOffDates(leg.cut_off_dates));
  }

  return out.sort((a, b) => {
    const ta = new Date(a.date_time!).getTime();
    const tb = new Date(b.date_time!).getTime();
    if (Number.isNaN(ta) || Number.isNaN(tb)) return 0;
    return ta - tb;
  });
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex justify-between gap-4 py-1.5">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium tabular-nums">{children}</dd>
    </div>
  );
}

interface ScheduleSailingDetailDialogProps {
  sailing: ScheduleResponse | null;
  areaMatcher: AreaMatcher;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rates valid on the sailing ETD (schedule lookup with include rates). */
  matchedRates?: MatchedScheduleRates | null;
}

export function ScheduleSailingDetailDialog({
  sailing,
  areaMatcher,
  open,
  onOpenChange,
  matchedRates,
}: ScheduleSailingDetailDialogProps) {
  const { preferences } = useUserPreferences();
  const showLocodes = preferences.showLocodes;

  if (!sailing) return null;

  const etd = sailingEtd(sailing);
  const eta = sailingEta(sailing);
  const transitDays = sailingTransitDays(sailing);
  const route = formatRouteLabel(
    {
      locode: sailing.origin_locode ?? "",
      name: sailing.origin_port_name,
    },
    {
      locode: sailing.dest_locode ?? "",
      name: sailing.dest_port_name,
    },
    showLocodes,
  );

  const originMatch = classifyAreaMatch(areaMatcher, sailing.origin_locode);
  const showOriginMatch = areaMatcher.hasExpansion;

  const allCutoffs = collectCutoffs(sailing);
  const sortedLegs = [...sailing.legs].sort(
    (a, b) => a.leg_order - b.leg_order,
  );
  const primaryVessel = sortedLegs.find((l) => l.vessel_name?.trim())?.vessel_name?.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="glass-overlay flex max-h-[min(85dvh,var(--app-vvh,85vh))] flex-col overflow-hidden rounded-2xl border-border/60 p-0 shadow-2xl"
        aria-describedby={undefined}
      >
        <div className="flex shrink-0 items-start gap-3 border-b border-border/50 px-5 py-4">
          <DialogHeader className="min-w-0 flex-1 space-y-1.5 text-left">
            <DialogTitle className="flex flex-wrap items-center gap-2 text-base font-semibold leading-tight">
              <CarrierLogo carrier={carrierCode(sailing)} bold />
              <Badge variant={sailing.direct ? "default" : "info"}>
                {sailing.direct
                  ? "Direct"
                  : `Transshipment · ${sailing.legs.length} legs`}
              </Badge>
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {route}
              {sailing.transit_time != null && (
                <span> · {sailing.transit_time} days transit</span>
              )}
              {primaryVessel && (
                <span className="block text-xs text-muted-foreground/90 sm:inline sm:before:content-['·_']">
                  {primaryVessel}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogClose
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
            aria-label="Close"
          >
            <X className="size-4" />
          </DialogClose>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <dl className="rounded-lg border border-border/50 bg-background/40 px-3 py-1 text-sm">
            <DetailRow label="ETD">
              {formatBookingDate(etd ?? undefined)}
            </DetailRow>
            <DetailRow label="ETA">
              {formatBookingDate(eta ?? undefined)}
            </DetailRow>
            {transitDays != null && (
              <DetailRow label="Transit time">
                {formatTransitDays(transitDays)}
              </DetailRow>
            )}
            {showOriginMatch && (
              <div className="flex justify-between gap-4 border-t border-border/40 py-1.5">
                <dt className="shrink-0 text-muted-foreground">Origin match</dt>
                <dd className="text-right">
                  {originMatch.kind === "area" ? (
                    <PortAreaMatchBadge
                      locode={sailing.origin_locode}
                      matcher={areaMatcher}
                    />
                  ) : originMatch.kind === "exact" ? (
                    <span className="font-medium">Exact port</span>
                  ) : (
                    <span className="text-muted-foreground">Other port</span>
                  )}
                </dd>
              </div>
            )}
          </dl>

          <ScheduleSailingMatchedRatesPanel matched={matchedRates} />

          {sortedLegs.length > 0 && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Voyage legs
              </h3>
              <ol className="flex flex-col gap-2">
                {sortedLegs.map((leg: ScheduleLegResponse) => (
                  <li
                    key={leg.leg_order}
                    className="rounded-lg border border-border/50 bg-background/40 px-3 py-2.5"
                  >
                    <p className="text-sm font-medium">
                      Leg {leg.leg_order}
                      {leg.vessel_name?.trim()
                        ? ` · ${leg.vessel_name.trim()}`
                        : ""}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {formatLegPort(
                        leg.departure_locode,
                        leg.departure_port_name,
                        showLocodes,
                      )}{" "}
                      {formatBookingDate(
                        leg.departure_estimated_date?.slice(0, 10),
                      )}{" "}
                      →{" "}
                      {formatLegPort(
                        leg.arrival_locode,
                        leg.arrival_port_name,
                        showLocodes,
                      )}{" "}
                      {formatBookingDate(
                        leg.arrival_estimated_date?.slice(0, 10),
                      )}
                    </p>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {(allCutoffs.length > 0 || sailing.cargo_cutoff) && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Cutoffs
              </h3>
              <ul className="rounded-lg border border-border/50 bg-background/40 px-3 py-1 text-sm">
                {sailing.cargo_cutoff && (
                  <li className="flex justify-between gap-4 py-1.5">
                    <span className="text-muted-foreground">Cargo cutoff</span>
                    <span className="tabular-nums">
                      {formatDateTimePlain(sailing.cargo_cutoff)}
                    </span>
                  </li>
                )}
                {allCutoffs.map((c, i) => (
                  <li
                    key={`${c.name ?? "cutoff"}-${c.date_time}-${i}`}
                    className="flex justify-between gap-4 py-1.5"
                  >
                    <span className="text-muted-foreground">
                      {normalizeCutoffName(c.name) || "Cutoff"}
                    </span>
                    <span className="tabular-nums">
                      {formatDateTimePlain(c.date_time!)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
